import type { ProductExportQuery } from '@/api/handlers/resources/products';
import {
    OPEN_PRODUCT_LIST_RANGE,
    PRODUCT_RANGE_ENDS,
    isProductListRangeInverted,
    type ProductFilterGroup,
    type ProductListDrawerFilters,
    type ProductRangeEnd,
    type ProductRangeFilter,
} from './productFilterTypes';
import { nameOfOption, type NamedOption } from './namedOption';
import {
    ALL_FILTER_VALUE,
    PRODUCT_TYPE_CHIP,
    PRODUCT_TYPE_OPTIONS,
} from './productsMeta';
import type { AppliedFilterTag } from './productFilters/appliedFilterTag';
import {
    categorySelectionLabel,
    describeCategorySelection,
    type CategoryTree,
} from './productFilters/categoryPickerMeta';
import { FILTERS_COPY, type FilterWords } from './productFilters/filterLabels';
import {
    MONEY_RANGE_VALUE,
    QUANTITY_RANGE_VALUE,
    describeRangeValue,
    type RangeValueCodec,
} from './productFilters/rangeValueMeta';

type TagOptions = {
    categoryTree: CategoryTree;
    brandOptions: readonly NamedOption[];
    branchOptions: readonly NamedOption[];
};

type ProductFilterDescriptor = {
    group: ProductFilterGroup;
    /** Preserve the established URL ordering independently of drawer order. */
    addressOrder: number;
    addressKeys: readonly string[];
    read: (params: URLSearchParams) => Partial<ProductListDrawerFilters>;
    write: (params: URLSearchParams, filters: ProductListDrawerFilters) => void;
    /** Both list and export use these exact fields and conversions. */
    toRequest: (filters: ProductListDrawerFilters) => ProductExportQuery;
    isActive: (filters: ProductListDrawerFilters) => boolean;
    affectsRows: boolean;
    /** Branch scope already has a dedicated line in the workbook. */
    exportAsFilter: boolean;
    cleared: Partial<ProductListDrawerFilters>;
    nameKey: string;
    buildTag: (
        filters: ProductListDrawerFilters,
        options: TagOptions,
    ) => AppliedFilterTag | null;
    describeTag: (tag: AppliedFilterTag) => FilterWords[];
};

const toId = (value: string | null): string | null => {
    const id = value?.trim() ?? '';
    return id && id !== ALL_FILTER_VALUE ? id : null;
};

const readIds = (params: URLSearchParams, key: string): string[] => [
    ...new Set(
        params
            .getAll(key)
            .map(toId)
            .filter((id) => id !== null),
    ),
];

const knownOrUnknown = (name: string | null): FilterWords =>
    name === null ? { key: `${FILTERS_COPY}.unknownValue` } : { text: name };

type RangeRequestKey = keyof Pick<
    ProductExportQuery,
    | 'minPrice'
    | 'maxPrice'
    | 'minCost'
    | 'maxCost'
    | 'minQuantity'
    | 'maxQuantity'
>;

type RangeDescriptor = ProductFilterDescriptor & {
    group: ProductRangeFilter;
    range: { codec: RangeValueCodec; legendKey: string };
};

const rangeDescriptor = (
    group: ProductRangeFilter,
    addressOrder: number,
    keys: Record<ProductRangeEnd, RangeRequestKey>,
    codec: RangeValueCodec,
): RangeDescriptor => ({
    group,
    addressOrder,
    addressKeys: Object.values(keys),
    read: (params) => {
        const readEnd = (end: ProductRangeEnd) => {
            const text = params.get(keys[end]);
            return text === null ? null : codec.read(text);
        };
        const range = { min: readEnd('min'), max: readEnd('max') };
        return {
            [group]: isProductListRangeInverted(range)
                ? OPEN_PRODUCT_LIST_RANGE
                : range,
        };
    },
    write: (params, filters) => {
        PRODUCT_RANGE_ENDS.forEach((end) => {
            const bound = filters[group][end];
            if (bound !== null) {
                params.set(keys[end], codec.write(bound));
            }
        });
    },
    toRequest: (filters) => {
        const query: ProductExportQuery = {};
        PRODUCT_RANGE_ENDS.forEach((end) => {
            const bound = filters[group][end];
            if (bound !== null) {
                query[keys[end]] = codec.toRequest(bound);
            }
        });
        return query;
    },
    isActive: (filters) =>
        filters[group].min !== null || filters[group].max !== null,
    affectsRows: true,
    exportAsFilter: true,
    cleared: { [group]: OPEN_PRODUCT_LIST_RANGE },
    nameKey: `${FILTERS_COPY}.tags.${group}`,
    buildTag: (filters) => ({ group, range: filters[group] }),
    describeTag: (tag) => {
        const words =
            'range' in tag ? describeRangeValue(tag.range, codec.format) : null;
        return words === null ? [] : [words];
    },
    range: { codec, legendKey: `${FILTERS_COPY}.${group}Legend` },
});

/**
 * Each group is declared once, in drawer/tag order. URL, request, badge,
 * clearing and tag consumers iterate this registry without knowing its fields.
 */
export const PRODUCT_FILTER_DESCRIPTORS = {
    branch: {
        group: 'branch',
        addressOrder: 8,
        addressKeys: ['branch'],
        read: (params) => ({ branchId: toId(params.get('branch')) }),
        write: (params, filters) => {
            if (filters.branchId) {
                params.set('branch', filters.branchId);
            }
        },
        toRequest: (filters) =>
            filters.branchId ? { branchId: filters.branchId } : {},
        isActive: (filters) => filters.branchId !== null,
        affectsRows: false,
        exportAsFilter: false,
        cleared: { branchId: null },
        nameKey: `${FILTERS_COPY}.tags.branch`,
        buildTag: (filters, options) =>
            filters.branchId === null
                ? null
                : {
                      group: 'branch',
                      name: nameOfOption(
                          options.branchOptions,
                          filters.branchId,
                      ),
                  },
        describeTag: (tag) =>
            tag.group === 'branch' ? [knownOrUnknown(tag.name)] : [],
    },
    category: {
        group: 'category',
        addressOrder: 1,
        addressKeys: ['category', 'noCategory'],
        read: (params) => ({
            categoryIds: readIds(params, 'category'),
            noCategory: params.get('noCategory') === 'true',
        }),
        write: (params, filters) => {
            filters.categoryIds.forEach((id) => params.append('category', id));
            if (filters.noCategory) {
                params.set('noCategory', 'true');
            }
        },
        toRequest: (filters) => ({
            ...(filters.categoryIds.length > 0
                ? { categoryIds: filters.categoryIds }
                : {}),
            ...(filters.noCategory ? { noCategory: true } : {}),
        }),
        isActive: (filters) =>
            filters.categoryIds.length > 0 || filters.noCategory,
        affectsRows: true,
        exportAsFilter: true,
        cleared: { categoryIds: [], noCategory: false },
        nameKey: `${FILTERS_COPY}.tags.category`,
        buildTag: (filters, options) => ({
            group: 'category',
            values: describeCategorySelection(filters, options.categoryTree),
        }),
        describeTag: (tag) =>
            tag.group === 'category'
                ? tag.values.map(categorySelectionLabel)
                : [],
    },
    brand: {
        group: 'brand',
        addressOrder: 2,
        addressKeys: ['brand', 'noBrand'],
        read: (params) => ({
            brandIds: readIds(params, 'brand'),
            noBrand: params.get('noBrand') === 'true',
        }),
        write: (params, filters) => {
            filters.brandIds.forEach((id) => params.append('brand', id));
            if (filters.noBrand) {
                params.set('noBrand', 'true');
            }
        },
        toRequest: (filters) => ({
            ...(filters.brandIds.length > 0
                ? { brandIds: filters.brandIds }
                : {}),
            ...(filters.noBrand ? { noBrand: true } : {}),
        }),
        isActive: (filters) => filters.brandIds.length > 0 || filters.noBrand,
        affectsRows: true,
        exportAsFilter: true,
        cleared: { brandIds: [], noBrand: false },
        nameKey: `${FILTERS_COPY}.tags.brand`,
        buildTag: (filters, options) => ({
            group: 'brand',
            names: filters.brandIds.map((id) =>
                nameOfOption(options.brandOptions, id),
            ),
            noBrand: filters.noBrand,
        }),
        describeTag: (tag) =>
            tag.group === 'brand'
                ? [
                      ...(tag.noBrand
                          ? [{ key: `${FILTERS_COPY}.noBrand` }]
                          : []),
                      ...tag.names.map(knownOrUnknown),
                  ]
                : [],
    },
    type: {
        group: 'type',
        addressOrder: 0,
        addressKeys: ['type'],
        read: (params) => ({
            type:
                PRODUCT_TYPE_OPTIONS.find(
                    (option) => option === params.get('type'),
                ) ?? null,
        }),
        write: (params, filters) => {
            if (filters.type) {
                params.set('type', filters.type);
            }
        },
        toRequest: (filters) => (filters.type ? { type: filters.type } : {}),
        isActive: (filters) => filters.type !== null,
        affectsRows: true,
        exportAsFilter: true,
        cleared: { type: null },
        nameKey: `${FILTERS_COPY}.tags.type`,
        buildTag: (filters) =>
            filters.type === null
                ? null
                : { group: 'type', value: filters.type },
        describeTag: (tag) =>
            tag.group === 'type'
                ? [{ key: PRODUCT_TYPE_CHIP[tag.value].labelKey }]
                : [],
    },
    price: rangeDescriptor(
        'price',
        5,
        { min: 'minPrice', max: 'maxPrice' },
        MONEY_RANGE_VALUE,
    ),
    cost: rangeDescriptor(
        'cost',
        6,
        { min: 'minCost', max: 'maxCost' },
        MONEY_RANGE_VALUE,
    ),
    quantity: rangeDescriptor(
        'quantity',
        7,
        { min: 'minQuantity', max: 'maxQuantity' },
        QUANTITY_RANGE_VALUE,
    ),
    lowStock: {
        group: 'lowStock',
        addressOrder: 3,
        addressKeys: ['lowStock'],
        read: (params) => ({ lowStock: params.get('lowStock') === 'true' }),
        write: (params, filters) => {
            if (filters.lowStock) {
                params.set('lowStock', 'true');
            }
        },
        toRequest: (filters) => (filters.lowStock ? { lowStock: true } : {}),
        isActive: (filters) => filters.lowStock,
        affectsRows: true,
        exportAsFilter: true,
        cleared: { lowStock: false },
        nameKey: `${FILTERS_COPY}.tags.lowStock`,
        buildTag: () => ({ group: 'lowStock' }),
        describeTag: () => [],
    },
    archived: {
        group: 'archived',
        addressOrder: 4,
        addressKeys: ['archived'],
        read: (params) => ({
            includeArchived: params.get('archived') === 'true',
        }),
        write: (params, filters) => {
            if (filters.includeArchived) {
                params.set('archived', 'true');
            }
        },
        toRequest: (filters) =>
            filters.includeArchived ? { includeArchived: true } : {},
        isActive: (filters) => filters.includeArchived,
        affectsRows: true,
        exportAsFilter: true,
        cleared: { includeArchived: false },
        nameKey: `${FILTERS_COPY}.tags.archived`,
        buildTag: () => ({ group: 'archived' }),
        describeTag: () => [],
    },
    // Only drafts (bos-app#226). Off sends nothing: the catalog lists drafts
    // with the rest by default, so `draft=false` is never asked for.
    draft: {
        group: 'draft',
        addressOrder: 9,
        addressKeys: ['draft'],
        read: (params) => ({ draft: params.get('draft') === 'true' }),
        write: (params, filters) => {
            if (filters.draft) {
                params.set('draft', 'true');
            }
        },
        toRequest: (filters) => (filters.draft ? { draft: true } : {}),
        isActive: (filters) => filters.draft,
        affectsRows: true,
        exportAsFilter: true,
        cleared: { draft: false },
        nameKey: `${FILTERS_COPY}.tags.draft`,
        buildTag: () => ({ group: 'draft' }),
        describeTag: () => [],
    },
} satisfies Record<ProductFilterGroup, ProductFilterDescriptor>;

export const PRODUCT_FILTER_DESCRIPTOR_LIST: readonly ProductFilterDescriptor[] =
    Object.values(PRODUCT_FILTER_DESCRIPTORS);

export const PRODUCT_RANGE_DESCRIPTORS = Object.values(
    PRODUCT_FILTER_DESCRIPTORS,
).filter((descriptor): descriptor is RangeDescriptor => 'range' in descriptor);

/** Registry-owned patches collectively cover the complete drawer shape. */
export const collectProductFilterFields = (
    select: (
        descriptor: ProductFilterDescriptor,
    ) => Partial<ProductListDrawerFilters>,
): ProductListDrawerFilters =>
    PRODUCT_FILTER_DESCRIPTOR_LIST.reduce<Partial<ProductListDrawerFilters>>(
        (fields, descriptor) => ({ ...fields, ...select(descriptor) }),
        {},
    ) as ProductListDrawerFilters;
